import React, { useEffect, useRef, useState } from 'react'
import { t } from 'ttag'
import { ReactComponent as CelebrationStepsIcon } from '../../assets/v2/icons/celebration-steps.svg'
import { ReactComponent as CelebrationFormulaIcon } from '../../assets/v2/icons/celebration-formula.svg'
import { ReactComponent as CelebrationBtnIcon } from '../../assets/v2/icons/celebration-btn.svg'
import { ActivityProgress, ProgressType } from '../../services/api.types'
import './YumUserProgressCelebration.scss'
import Modal from '@material-ui/core/Modal'
import Backdrop from '@material-ui/core/Backdrop'
import Fade from '@material-ui/core/Fade'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { formatThousands } from '../../common/helpers/translations'
import { Haptics } from '@capacitor/haptics'
import { isNative } from '../../common/helpers/isNative'
import ConfettiExplosion from 'react-confetti-explosion'
import { RateApp } from 'capacitor-rate-app'

export interface IUserProgressProps {
  activityProgress: ActivityProgress
  progressType: ProgressType
  manualEntryEnabled?: boolean
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

function usePrevious(value: any) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export default function YumUserProgressCelebration(props: IUserProgressProps) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [stepsDifference, setStepsDifference] = useState(0)
  const [percentageDifference, setPercentageDifference] = useState(0)
  const [isExploding, setIsExploding] = React.useState(false)

  const {
    progressType,
    activityProgress: { goalId },
    manualEntryEnabled,
  } = props

  const handleOpen = () => {
    setTimeout(() => {
      setOpen(true)
      if (isNative()) Haptics.notification()
    }, 500)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const CELEBRATE_STEPS_LIMIT = 5000
  const CELEBRATE_FORMULA_LIMIT = 3
  const CONFETTI_DURATION = 3500

  const prevStepsTotal = usePrevious(props.activityProgress.stepsTotal)
  const prevTotalPercentage = usePrevious(props.activityProgress.totalPercentage)

  useEffect(() => {
    // TODO: First check if goal was just achieved.
    const totalPercentage = props.activityProgress.totalPercentage
    if (Number(prevTotalPercentage) < 100 && totalPercentage >= 100) {
      // Goal completion celebration.
      setIsExploding(true)
      if (isNative()) Haptics.notification().then()
      setTimeout(() => {
        // Hide the confetti below the fold.
        setIsExploding(false)
        // Ask for a rating. But ultimately the platform decides if it should show the rate app dialog.
        RateApp.requestReview().then()
      }, CONFETTI_DURATION)
    } else {
      // Currently only show this if manual entry is disabled.
      if (!manualEntryEnabled) {
        // Progress celebration.
        if (progressType === ProgressType.STEPS) {
          const stepsTotal = props.activityProgress.stepsTotal
          const diff = stepsTotal - Number(prevStepsTotal)
          if (Number(prevStepsTotal) > 0 && stepsTotal > Number(prevStepsTotal) && diff >= CELEBRATE_STEPS_LIMIT) {
            setStepsDifference(diff)
            handleOpen()
          }
        } else if (progressType === ProgressType.ALL) {
          const totalPercentage = props.activityProgress.totalPercentage
          const diff = totalPercentage - Number(prevTotalPercentage)
          if (
            Number(prevTotalPercentage) > 0 &&
            totalPercentage > Number(prevTotalPercentage) &&
            diff >= CELEBRATE_FORMULA_LIMIT
          ) {
            setPercentageDifference(diff)
            handleOpen()
          }
        }
      }
    }
  }, [
    goalId,
    progressType,
    props.activityProgress.stepsTotal,
    props.activityProgress.totalPercentage,
    prevStepsTotal,
    prevTotalPercentage,
  ])

  return (
    <>
      {isExploding ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'inline-block', position: 'absolute' }}>
            <ConfettiExplosion duration={CONFETTI_DURATION} particleCount={150} zIndex={1} />
          </div>
        </div>
      ) : (
        ''
      )}
      <Modal
        className={classes.modal}
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <div className="user-progress-celebration" onClick={handleClose}>
            <div>
              <div className="user-progress-celebration__subtitle">
                {progressType === ProgressType.STEPS ? t`New steps` : t`New points`}
              </div>
              <div className="user-progress-celebration__message">{t`You made great progress!`}</div>
              <div className="user-progress-celebration__stats">
                {progressType === ProgressType.STEPS ? (
                  <>
                    <CelebrationStepsIcon /> <strong>+{formatThousands(stepsDifference)}</strong> {t`steps`}
                  </>
                ) : (
                  <>
                    <CelebrationFormulaIcon /> <strong>+{percentageDifference}%</strong> {t`of activity`}
                  </>
                )}
              </div>
              <CelebrationBtnIcon className="user-progress-celebration__btn" />
            </div>
          </div>
        </Fade>
      </Modal>
    </>
  )
}
